import React, { useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Modal } from 'react-bootstrap';
import moment from 'moment';
import { Icon, ICONS } from 'components/Icons';
import ActionLink from 'components/elements/molecules/ActionLink/ActionLink';
import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';
import Checkbox from 'components/elements/atoms/Checkbox/Checkbox';
import TopicSuggestedReadings from 'routes/Portfolio/Component/CoursesTab/Topic/components/TopicSuggestedReadings';
import * as Actions from 'redux/actions';
import './ReviewThisWeekSuggestedReadings.scss';
import ToolTip from 'components/elements/atoms/ToolTip/ToolTip';

function ReviewRow({ row, selectedArticles, onSelectArticle }) {
  return (
    <div className="review-week-row">
      <div className="review-week-row-header d-flex align-items-center">
        <div className="flex-fill">
          <div className="review-week-date font-size-16">
            <em>
              <strong>{moment(row.date).format('MMM D, YYYY')}</strong>
              {': '}
              {row.topic || row.name}
            </em>
          </div>
        </div>
      </div>
      <div className="">
        <TopicSuggestedReadings
          syllabusRow={row}
          selectedArticles={selectedArticles}
          onSelectArticle={onSelectArticle}
          showCheckbox
          showBanner={false}
          showTitle={false}
        />
      </div>
    </div>
  );
}

function ReviewThisWeekSuggestedReadings({
  onClose,
  currentWeekRows,
  otherRows,
  selectedCourse,
  actions,
  topicId,
}) {
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const sortedOtherRows = useMemo(
    () =>
      [...otherRows].sort(
        (a, b) => moment(b.date).valueOf() - moment(a.date).valueOf(),
      ),
    [otherRows],
  );

  const activeRows = useMemo(
    () =>
      topicId
        ? [...currentWeekRows, ...otherRows].filter(
            (row) => row.courseTopicId === topicId,
          )
        : [...currentWeekRows, ...sortedOtherRows],
    [topicId, currentWeekRows, otherRows, sortedOtherRows],
  );

  const allArticleIds = useMemo(
    () => activeRows.flatMap((row) => (row.readings || []).map((r) => r._id)),
    [activeRows],
  );

  const approvedCount = useMemo(
    () =>
      activeRows
        .flatMap((row) => row.readings || [])
        .filter((r) => r.status === 'approved').length,
    [activeRows],
  );

  const reviewedCount = useMemo(
    () =>
      activeRows
        .flatMap((row) => row.readings || [])
        .filter((r) => r.status === 'approved' || r.status === 'rejected')
        .length,
    [activeRows],
  );

  const draftCount = useMemo(
    () =>
      activeRows
        .flatMap((row) => row.readings || [])
        .filter((r) => r.status === 'draft').length,
    [activeRows],
  );

  const isAllSelected =
    allArticleIds.length > 0 &&
    allArticleIds.every((id) => selectedArticles.includes(id));

  const handleSelectArticle = useCallback((articleId) => {
    setSelectedArticles((prev) =>
      prev.includes(articleId)
        ? prev.filter((id) => id !== articleId)
        : [...prev, articleId],
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedArticles(isAllSelected ? [] : allArticleIds);
  }, [isAllSelected, allArticleIds]);

  const readingRowMap = useMemo(() => {
    const map = {};
    activeRows.forEach((row) => {
      (row.readings || []).forEach((r) => {
        map[r._id] = row._id;
      });
    });
    return map;
  }, [activeRows]);

  const updateSelectedStatus = useCallback(
    (status) => {
      selectedArticles.forEach((readingId) => {
        const rowId = readingRowMap[readingId];
        if (rowId) {
          actions.updateSyllabusReadingStatus(
            selectedCourse,
            rowId,
            readingId,
            status,
          );
        }
      });
    },
    [selectedArticles, readingRowMap, selectedCourse, actions],
  );

  const handleAcceptSelected = useCallback(() => {
    updateSelectedStatus('approved');
    setSelectedArticles([]);
  }, [updateSelectedStatus]);

  const handleRejectSelected = useCallback(() => {
    updateSelectedStatus('rejected');
    setSelectedArticles([]);
  }, [updateSelectedStatus]);

  const handlePublish = useCallback(async () => {
    for (const row of activeRows) {
      if ((row.readings || []).some((r) => r.status === 'approved')) {
        if (topicId) {
          const approvedReadings = (row.readings || []).filter(
            (r) => r.status === 'approved',
          );
          actions.optimisticLoadTopicReadings(
            selectedCourse,
            topicId,
            approvedReadings,
          );
        }

        await actions.updateSyllabusRowStatus(
          selectedCourse,
          row._id,
          'published',
        );

        if (topicId) {
          await actions.getTopicReadings(selectedCourse, topicId);
          await actions.getPendingActions(topicId, 'courseTopic');
        }
      }
    }
  }, [activeRows, selectedCourse, actions, topicId]);

  const renderPublishBtn = () => {
    const btn = (
      <RectangularButton
        darkButton
        onClick={() => setShowConfirm(true)}
        disabled={approvedCount < 1}
      >
        Publish to students
      </RectangularButton>
    );
    if (approvedCount < 1) {
      return (
        <ToolTip
          toolTipText="You can publish after you’ve approved at least 1 reading for the week."
          toolTipPosition="left"
        >
          {btn}
        </ToolTip>
      );
    }
    return btn;
  };

  return (
    <>
      <Modal
        show={true}
        className="review-this-week-modal"
        onHide={onClose}
        bsSize="lg"
      >
        <Modal.Header>
          <button
            type="button"
            onClick={onClose}
            className="btn-av-container position-absolute mr-3 mt-4 close"
            aria-label="Close"
          >
            <Icon icon={ICONS.CLOSE} />
          </button>
          <div className="modal-title font-weight-600">
            {topicId
              ? 'Review Suggested Readings'
              : "Review This Week's Suggested Readings"}
          </div>
          {allArticleIds.length > 0 && (
            <>
              <div className="mt-2 d-flex align-items-center">
                <span className="review-week-reviewed-count ml-auto mr-5">
                  {reviewedCount} of {allArticleIds.length} reviewed
                </span>
                {renderPublishBtn()}
              </div>
              {/* <div className="review-week-actions mt-2 d-flex align-items-center"> */}
              {/* <Checkbox
                  checked={isAllSelected}
                  handleChange={handleSelectAll}
                  ariaLabel="Select all articles"
                  labelClassName="pl-0"
                  className="mb-0"
                >
                  Select all
                </Checkbox> */}
              {/* {selectedArticles.length > 0 && (
                  <>
                    <span className="mx-2">|</span>
                    <span>{selectedArticles.length} selected</span>
                  </>
                )}
                {selectedArticles.length > 0 && (
                  <>
                    <span className="mx-2">|</span>
                    <ActionLink
                      label="Approve"
                      onClick={handleAcceptSelected}
                    />
                    <ActionLink
                      label="Reject"
                      onClick={handleRejectSelected}
                      linkClassName="danger ml-2"
                    />
                  </>
                )}
              </div> */}
            </>
          )}
        </Modal.Header>
        <Modal.Body>
          {topicId ? (
            activeRows.map((row) => (
              <ReviewRow
                key={row._id}
                row={row}
                selectedArticles={selectedArticles}
                onSelectArticle={handleSelectArticle}
              />
            ))
          ) : (
            <>
              {currentWeekRows.some(
                (row) => (row.readings || []).length > 0,
              ) && (
                <>
                  {/* <div className="review-week-section-divider">This Week</div> */}
                  {currentWeekRows.map((row) => (
                    <ReviewRow
                      key={row._id}
                      row={row}
                      selectedArticles={selectedArticles}
                      onSelectArticle={handleSelectArticle}
                    />
                  ))}
                </>
              )}
              {sortedOtherRows.length > 0 && (
                <>
                  {currentWeekRows.some(
                    (row) => (row.readings || []).length > 0,
                  ) && (
                    <div className="review-week-section-divider">
                      {`Other Lecture${sortedOtherRows.length !== 1 ? 's' : ''} (${sortedOtherRows.length})`}
                    </div>
                  )}
                  {sortedOtherRows.map((row) => (
                    <ReviewRow
                      key={row._id}
                      row={row}
                      selectedArticles={selectedArticles}
                      onSelectArticle={handleSelectArticle}
                    />
                  ))}
                </>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
      {showConfirm && (
        <ShowPublishConfirmModal
          approvedCount={approvedCount}
          draftCount={draftCount}
          onConfirm={handlePublish}
          onCancel={() => setShowConfirm(false)}
          onClose={onClose}
        />
      )}
    </>
  );
}

function ShowPublishConfirmModal({
  approvedCount,
  draftCount,
  onConfirm,
  onCancel,
  onClose,
}) {
  const [published, setPublished] = useState(false);

  const handleConfirm = () => {
    onConfirm();
    setPublished(true);
  };

  if (published) {
    return (
      <Modal show={true} className="publish-confirm-modal" onHide={onCancel}>
        <Modal.Header>
          <div className="modal-title font-weight-600">Published!</div>
        </Modal.Header>
        <Modal.Body>
          <p>
            You've published {approvedCount} article
            {approvedCount !== 1 ? 's' : ''} for this week's lecture
            {approvedCount !== 1 ? 's' : ''}.
          </p>
          <p className="gray-4">
            <Icon icon={ICONS.CHECK} className="publish-success-check" />
            Students will receive an email soon.
          </p>
          <p className="gray-4">
            <Icon icon={ICONS.CHECK} className="publish-success-check" />
            You can publish additional articles at any time.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <RectangularButton
            darkButton
            onClick={() => {
              onCancel();
              onClose();
            }}
          >
            Okay
          </RectangularButton>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal show={true} className="publish-confirm-modal" onHide={onCancel}>
      <Modal.Header>
        <div className="modal-title font-weight-600">Publish to students</div>
      </Modal.Header>
      <Modal.Body>
        <p>
          You are about to publish {approvedCount} article
          {approvedCount !== 1 ? 's' : ''} to students.
        </p>
        {draftCount > 0 && (
          <p className="gray-4">
            You still have {draftCount} article{draftCount !== 1 ? 's' : ''} to
            review.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <RectangularButton onClick={onCancel}>Cancel</RectangularButton>
        <RectangularButton darkButton onClick={handleConfirm} className="ml-3">
          Publish {approvedCount} article{approvedCount !== 1 ? 's' : ''} now
        </RectangularButton>
      </Modal.Footer>
    </Modal>
  );
}

function mapStateToProps(state, ownProps) {
  const rows = state.syllabus?.currentSyllabus?.syllabus?.rows || [];
  const startOfWeek = moment().startOf('week');
  const endOfWeek = moment().endOf('week');
  const currentWeekRows = rows.filter((row) => {
    if (!row.date) return false;
    if (!row.courseTopicId) return false;
    return moment(row.date).isBetween(startOfWeek, endOfWeek, null, '[]');
  });
  const otherRows = rows.filter((row) => {
    if (!row.date) return false;
    if (!row.courseTopicId) return false;
    if (moment(row.date).isBetween(startOfWeek, endOfWeek, null, '[]'))
      return false;
    return (row.readings || []).some((r) => r.status === 'draft');
  });

  return {
    currentWeekRows,
    otherRows,
    selectedCourse: state.classRoom.selectedCourse || null,
    topicId: ownProps.topicId || null,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ReviewThisWeekSuggestedReadings);
